/**
 * Convert a string to PascalCase.
 * @param {string} str - The string to convert to PascalCase.
 * @returns {string} The string in PascalCase.
 *
 * @example
 * toPascalCase("hello world") // "HelloWorld"
 * toPascalCase("HELLO WORLD") // "HelloWorld"
 * toPascalCase("my component name") // "MyComponentName"
 * toPascalCase("MY COMPONENT NAME") // "MyComponentName"
 * toPascalCase("my-component-name") // "MyComponentName"
 * toPascalCase("my_component_name") // "MyComponentName"
 * toPascalCase("myComponentName") // "MyComponentName"
 * toPascalCase("my-component-name") // "MyComponentName"
 */
export function toPascalCase(str) {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .split(" ")
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
}

export function toKebabCase(str) {
  return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * Convert a string to title case.
 * @param {string} str - The string to convert to title case.
 * @returns {string} The string in title case.
 *
 * @example
 * toTitleCase("hello world") // "Hello World"
 * toTitleCase("HELLO WORLD") // "Hello World"
 * toTitleCase("my component name") // "My Component Name"
 * toTitleCase("MY COMPONENT NAME") // "My Component Name"
 * toTitleCase("my-component-name") // "My Component Name"
 */
export function toTitleCase(str) {
  return str.replace(/\w+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

/**
 * Strip HTML tags from a string.
 * @param {string} str - The string to strip HTML tags from.
 * @returns {string} The string with HTML tags stripped.
 *
 * @example
 * stripHtml("<p>Hello World</p>") // "Hello World"
 * stripHtml("<div><span>Text</span></div>") // "Text"
 * stripHtml("<br>Hello<br>") // "Hello"
 * stripHtml('<img src="test.jpg">Text') // "Text"
 * stripHtml('<p class="test">Hello</p>') // "Hello"
 * stripHtml('<a href="http://example.com">Link</a>') // "Link"
 * stripHtml("<div><p><strong>Bold</strong> text</p></div>") // "Bold text"
 * stripHtml("Plain text") // "Plain text"
 * stripHtml("Text with < and > symbols") // "Text with  and  symbols"
 * stripHtml(null) // undefined
 * stripHtml(undefined) // undefined
 * stripHtml("") // ""
 */
export function stripHtml(str) {
  return str?.replace(/<[^>]*>?/gm, "");
}

/**
 * Generate a random UUID.
 * The fallback is used for browsers that don't support crypto.randomUUID.
 * This is a workaround since one of our legacy dependencies seem to override the global crypto object.
 * TODO: Remove this once we've migrated to a more modern dependency.
 * @see https://stackoverflow.com/a/2117523/102961
 */
export function randomUUID() {
  return crypto.randomUUID?.() ?? fallbackRandomUUID();
}

/**
 * Fallback for browsers that don't support crypto.randomUUID.
 * TODO: Remove this once we've migrated to a more modern dependency.
 * @returns {string} A random UUID.
 * @see https://stackoverflow.com/a/2117523/102961
 */
function fallbackRandomUUID() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
  );
}
